// Cognito / API 配置：从 Vite 环境变量读取（import.meta.env），并提供合理默认，
// 便于本地开发（localhost:5173）。生产值由部署时的 .env 注入（见 .env.example）。
//
// 设计依据：design.md「PWA 设计 → 认证」、infra 任务 4.5（Cognito User Pool + App Client）。

/** 运行期 Cognito / API 配置（全部为字符串，构建时注入或本地默认）。 */
export interface AuthConfig {
  /** Cognito Hosted UI 域名（完整 origin，例如 https://comfy-owner-xxx.auth.us-east-1.amazoncognito.com）。 */
  readonly cognitoDomain: string;
  /** App Client ID（公共客户端，PKCE，无密钥）。 */
  readonly clientId: string;
  /** OAuth 回调地址（须在 Cognito App Client 的 callbackUrls 中登记）。 */
  readonly redirectUri: string;
  /** 注销后跳转地址（须在 logoutUrls 中登记）。 */
  readonly logoutRedirectUri: string;
  /** 请求的 OAuth scopes。 */
  readonly scopes: readonly string[];
  /** API 基地址（API Gateway HTTP API），authedFetch 的相对路径以此为基。 */
  readonly apiBaseUrl: string;
  /** AWS region（仅用于诊断展示，可选）。 */
  readonly region: string;
  /** User Pool ID（仅用于诊断展示，可选）。 */
  readonly userPoolId: string;
}

// Vite 在构建时把 import.meta.env.VITE_* 静态替换为字符串字面量。
// 用可选链 + 兜底，确保即使未注入也能在本地跑通登录跳转（指向本地回调）。
type ViteEnv = Record<string, string | undefined>;

function readEnv(): ViteEnv {
  // import.meta.env 在 Vite 下始终存在；在纯 Node 测试下可能为 undefined。
  const env = (import.meta as unknown as { env?: ViteEnv }).env;
  return env ?? {};
}

function currentOrigin(fallback: string): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return fallback;
}

const DEV_ORIGIN = "http://localhost:5173";

/**
 * 解析配置。允许传入显式 env（便于测试），默认读取 import.meta.env。
 */
export function resolveAuthConfig(env: ViteEnv = readEnv()): AuthConfig {
  const origin = currentOrigin(DEV_ORIGIN);

  const cognitoDomain = (env.VITE_COGNITO_DOMAIN ?? "").replace(/\/+$/, "");
  const clientId = env.VITE_USER_POOL_CLIENT_ID ?? "";
  const redirectUri = env.VITE_REDIRECT_URI ?? `${origin}/auth/callback`;
  const logoutRedirectUri = env.VITE_LOGOUT_REDIRECT_URI ?? `${origin}/`;
  const scopesRaw = env.VITE_OAUTH_SCOPES ?? "openid email profile";
  const apiBaseUrl = (env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");
  const region = env.VITE_COGNITO_REGION ?? "";
  const userPoolId = env.VITE_USER_POOL_ID ?? "";

  return {
    cognitoDomain,
    clientId,
    redirectUri,
    logoutRedirectUri,
    scopes: scopesRaw.split(/\s+/).filter(Boolean),
    apiBaseUrl,
    region,
    userPoolId,
  };
}

/** 配置是否完整到足以发起 Hosted UI 登录。 */
export function isAuthConfigured(cfg: AuthConfig): boolean {
  return Boolean(cfg.cognitoDomain && cfg.clientId);
}
